import { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { buildImageUrl } from '../../utils/imageUrl';
import {
  Globe, Save, Plus, Trash2, Pencil, X, Image as ImageIcon, FileText,
  Layers, Package, ListOrdered, Quote, Inbox, Upload, Loader2, ExternalLink, Users
} from 'lucide-react';

const TABS = [
  { key: 'settings', label: 'Site Settings', icon: Globe },
  { key: 'homepage', label: 'Homepage', icon: Layers },
  { key: 'team', label: 'Our Team', icon: Users },
  { key: 'products', label: 'Products', icon: Package },
  { key: 'process', label: 'Process Steps', icon: ListOrdered },
  { key: 'testimonials', label: 'Testimonials', icon: Quote },
  { key: 'pages', label: 'Pages', icon: FileText },
  { key: 'media', label: 'Media Library', icon: ImageIcon },
  { key: 'enquiries', label: 'Enquiries', icon: Inbox },
];

function img(url) {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return buildImageUrl(url) || '';
}

// ── Reusable small components ─────────────────────────────────────────────
function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
const inputCls = "w-full border-2 border-gray-200 focus:border-amber-400 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none";

// Image picker: lets admin either paste a URL or upload a new image straight to Cloudinary
function ImagePicker({ value, onChange, label = 'Image' }) {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await api.post('/website/admin/media/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(res.data.url);
      toast.success('Image uploaded');
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Field label={label}>
      <div className="flex items-center gap-3">
        {value ? (
          <img src={img(value)} alt="" className="w-16 h-16 rounded-lg object-cover border border-gray-200 flex-shrink-0" />
        ) : (
          <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0"><ImageIcon className="w-5 h-5 text-gray-300" /></div>
        )}
        <div className="flex-1 space-y-2">
          <input value={value || ''} onChange={e => onChange(e.target.value)} placeholder="Image URL" className={inputCls} />
          <label className="inline-flex items-center gap-2 text-xs font-semibold text-amber-700 cursor-pointer hover:text-amber-800">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {uploading ? 'Uploading…' : 'Upload new image'}
            <input type="file" accept="image/*" className="hidden" onChange={e => handleUpload(e.target.files?.[0])} />
          </label>
        </div>
      </div>
    </Field>
  );
}

function GalleryPicker({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const images = Array.isArray(value) ? value : [];

  const handleUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append('image', file);
        const res = await api.post('/website/admin/media/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        uploaded.push(res.data.url);
      }
      onChange([...images, ...uploaded]);
      toast.success('Images uploaded');
    } catch {
      toast.error('Upload failed');
    } finally { setUploading(false); }
  };

  const remove = (idx) => onChange(images.filter((_, i) => i !== idx));

  return (
    <Field label="Additional Gallery Images">
      <div className="flex flex-wrap gap-3 mb-3">
        {images.map((url, i) => (
          <div key={i} className="relative w-16 h-16 flex-shrink-0">
            <img src={img(url)} alt="" className="w-full h-full object-cover rounded-lg border border-gray-200" />
            <button type="button" onClick={() => remove(i)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"><X className="w-3 h-3" /></button>
          </div>
        ))}
      </div>
      <label className="inline-flex items-center gap-2 text-xs font-semibold text-amber-700 cursor-pointer hover:text-amber-800">
        {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
        {uploading ? 'Uploading…' : 'Add gallery images'}
        <input type="file" accept="image/*" multiple className="hidden" onChange={e => handleUpload(e.target.files)} />
      </label>
    </Field>
  );
}

function SectionCard({ title, action, children }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-bold text-gray-900">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SETTINGS TAB
// ═══════════════════════════════════════════════════════════════════════════
function SettingsTab() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get('/website/public/settings').then(res => setForm(res.data)).catch(() => toast.error('Failed to load settings'));
  }, []);
  useEffect(load, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/website/admin/settings', form);
      toast.success('Website settings saved');
    } catch {
      toast.error('Failed to save');
    } finally { setSaving(false); }
  };

  if (!form) return <div className="text-gray-400 text-sm">Loading…</div>;

  return (
    <div className="space-y-6">
      <SectionCard title="Brand & Identity">
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Site Name"><input className={inputCls} value={form.site_name || ''} onChange={e => set('site_name', e.target.value)} /></Field>
          <Field label="Tagline"><input className={inputCls} value={form.tagline || ''} onChange={e => set('tagline', e.target.value)} /></Field>
          <ImagePicker label="Logo" value={form.logo_url} onChange={v => set('logo_url', v)} />
          <ImagePicker label="Favicon" value={form.favicon_url} onChange={v => set('favicon_url', v)} />
        </div>
      </SectionCard>

      <SectionCard title="Hero Section (Homepage banner)">
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Hero Heading"><textarea rows={2} className={inputCls} value={form.hero_heading || ''} onChange={e => set('hero_heading', e.target.value)} /></Field>
          <Field label="Hero Subheading"><textarea rows={2} className={inputCls} value={form.hero_subheading || ''} onChange={e => set('hero_subheading', e.target.value)} /></Field>
          <div className="md:col-span-2"><ImagePicker label="Hero Background Image" value={form.hero_image} onChange={v => set('hero_image', v)} /></div>
        </div>
      </SectionCard>

      <SectionCard title="Homepage Stats Bar">
        <div className="grid md:grid-cols-4 gap-5">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="space-y-2">
              <Field label={`Stat ${i} Number`}><input className={inputCls} value={form[`stat_${i}_num`] || ''} onChange={e => set(`stat_${i}_num`, e.target.value)} /></Field>
              <Field label={`Stat ${i} Label`}><input className={inputCls} value={form[`stat_${i}_label`] || ''} onChange={e => set(`stat_${i}_label`, e.target.value)} /></Field>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="Contact Details">
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Phone"><input className={inputCls} value={form.phone || ''} onChange={e => set('phone', e.target.value)} /></Field>
          <Field label="WhatsApp Number"><input className={inputCls} value={form.whatsapp || ''} onChange={e => set('whatsapp', e.target.value)} /></Field>
          <Field label="Email"><input className={inputCls} value={form.email || ''} onChange={e => set('email', e.target.value)} /></Field>
          <Field label="Business Hours"><input className={inputCls} value={form.business_hours || ''} onChange={e => set('business_hours', e.target.value)} /></Field>
          <div className="md:col-span-2"><Field label="Address"><textarea rows={2} className={inputCls} value={form.address || ''} onChange={e => set('address', e.target.value)} /></Field></div>
        </div>
      </SectionCard>

      <SectionCard title="Social Media (SMO)">
        <div className="grid md:grid-cols-3 gap-5">
          <Field label="Facebook URL"><input className={inputCls} value={form.facebook_url || ''} onChange={e => set('facebook_url', e.target.value)} /></Field>
          <Field label="Instagram URL"><input className={inputCls} value={form.instagram_url || ''} onChange={e => set('instagram_url', e.target.value)} /></Field>
          <Field label="LinkedIn URL"><input className={inputCls} value={form.linkedin_url || ''} onChange={e => set('linkedin_url', e.target.value)} /></Field>
        </div>
      </SectionCard>

      <SectionCard title="SEO Defaults">
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Default Meta Title"><input className={inputCls} value={form.seo_default_title || ''} onChange={e => set('seo_default_title', e.target.value)} /></Field>
          <Field label="Google Analytics ID"><input className={inputCls} value={form.google_analytics_id || ''} onChange={e => set('google_analytics_id', e.target.value)} placeholder="G-XXXXXXX" /></Field>
          <div className="md:col-span-2"><Field label="Default Meta Description"><textarea rows={2} className={inputCls} value={form.seo_default_description || ''} onChange={e => set('seo_default_description', e.target.value)} /></Field></div>
          <Field label="Default Meta Keywords"><input className={inputCls} value={form.seo_default_keywords || ''} onChange={e => set('seo_default_keywords', e.target.value)} /></Field>
          <ImagePicker label="Default Social Share Image (OG Image)" value={form.og_image} onChange={v => set('og_image', v)} />
        </div>
      </SectionCard>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-xl font-bold text-sm disabled:opacity-50">
          <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// HOMEPAGE SECTIONS TAB (currently: About block)
// ═══════════════════════════════════════════════════════════════════════════
function HomepageTab() {
  const [about, setAbout] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/website/admin/sections').then(res => {
      const found = res.data.find(s => s.section_key === 'about');
      setAbout(found || { section_key: 'about', title: '', subtitle: '', body: '', image_url: '', image_url_2: '' });
    }).catch(() => toast.error('Failed to load'));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/website/admin/sections/about`, about);
      toast.success('About section saved');
    } catch {
      toast.error('Failed to save');
    } finally { setSaving(false); }
  };

  if (!about) return <div className="text-gray-400 text-sm">Loading…</div>;

  return (
    <div className="space-y-6">
      <SectionCard title="About Section">
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Label / Eyebrow"><input className={inputCls} value={about.subtitle || ''} onChange={e => setAbout({ ...about, subtitle: e.target.value })} /></Field>
          <Field label="Heading"><input className={inputCls} value={about.title || ''} onChange={e => setAbout({ ...about, title: e.target.value })} /></Field>
          <div className="md:col-span-2"><Field label="Body Text"><textarea rows={5} className={inputCls} value={about.body || ''} onChange={e => setAbout({ ...about, body: e.target.value })} /></Field></div>
          <ImagePicker label="Main Image" value={about.image_url} onChange={v => setAbout({ ...about, image_url: v })} />
          <ImagePicker label="Secondary Image" value={about.image_url_2} onChange={v => setAbout({ ...about, image_url_2: v })} />
        </div>
      </SectionCard>
      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-xl font-bold text-sm disabled:opacity-50">
          <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// GENERIC REPEATER TAB (used for Products / Process / Testimonials)
// ═══════════════════════════════════════════════════════════════════════════
function RepeaterTab({ resource, emptyItem, fields, renderThumb, title }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // item being edited, or null
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/website/admin/${resource}`).then(res => setItems(res.data)).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  }, [resource]);
  useEffect(load, [load]);

  const startNew = () => setEditing({ ...emptyItem });
  const startEdit = (item) => {
    const parsed = { ...item };
    fields.forEach(f => {
      if (f.type === 'gallery') {
        try { parsed[f.key] = JSON.parse(item[f.key] || '[]'); } catch { parsed[f.key] = []; }
      }
    });
    setEditing(parsed);
  };
  const cancel = () => setEditing(null);

  const save = async () => {
    try {
      if (editing.id) {
        await api.put(`/website/admin/${resource}/${editing.id}`, editing);
      } else {
        await api.post(`/website/admin/${resource}`, editing);
      }
      toast.success('Saved');
      setEditing(null);
      load();
    } catch {
      toast.error('Failed to save');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await api.delete(`/website/admin/${resource}/${id}`);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <button onClick={startNew} className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-5 py-2.5 rounded-xl font-bold text-sm">
          <Plus className="w-4 h-4" /> Add {title}
        </button>
      </div>

      {editing && (
        <SectionCard title={editing.id ? `Edit ${title}` : `New ${title}`} action={<button onClick={cancel}><X className="w-4 h-4 text-gray-400" /></button>}>
          <div className="grid md:grid-cols-2 gap-5">
            {fields.map(f => (
              <div key={f.key} className={f.full ? 'md:col-span-2' : ''}>
                {f.type === 'image' ? (
                  <ImagePicker label={f.label} value={editing[f.key]} onChange={v => setEditing({ ...editing, [f.key]: v })} />
                ) : f.type === 'gallery' ? (
                  <GalleryPicker value={editing[f.key]} onChange={v => setEditing({ ...editing, [f.key]: v })} />
                ) : f.type === 'textarea' ? (
                  <Field label={f.label}><textarea rows={3} className={inputCls} value={editing[f.key] || ''} onChange={e => setEditing({ ...editing, [f.key]: e.target.value })} /></Field>
                ) : f.type === 'number' ? (
                  <Field label={f.label}><input type="number" className={inputCls} value={editing[f.key] ?? ''} onChange={e => setEditing({ ...editing, [f.key]: e.target.value })} /></Field>
                ) : (
                  <Field label={f.label}><input className={inputCls} value={editing[f.key] || ''} onChange={e => setEditing({ ...editing, [f.key]: e.target.value })} /></Field>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-end mt-5">
            <button onClick={save} className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-6 py-2.5 rounded-xl font-bold text-sm">
              <Save className="w-4 h-4" /> Save
            </button>
          </div>
        </SectionCard>
      )}

      {loading ? (
        <div className="text-gray-400 text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-gray-400 text-sm bg-white rounded-2xl border border-gray-100 p-8 text-center">No {title.toLowerCase()}s yet — add your first one above.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map(item => (
            <div key={item.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex gap-4">
              {renderThumb(item)}
              <div className="flex-1 min-w-0">
                <div className="font-bold text-gray-900 text-sm truncate">{item.name || item.title || item.client_name}</div>
                <div className="text-xs text-gray-500 line-clamp-2 mt-1">{item.description || item.quote || item.category}</div>
                <div className="flex gap-3 mt-3">
                  <button onClick={() => startEdit(item)} className="text-xs font-semibold text-amber-700 flex items-center gap-1"><Pencil className="w-3 h-3" /> Edit</button>
                  <button onClick={() => remove(item.id)} className="text-xs font-semibold text-red-600 flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const ProductsTab = () => (
  <RepeaterTab
    resource="products"
    title="Product"
    emptyItem={{ category: '', name: '', description: '', image_url: '', material: '', dimensions: '', gallery_images: [], sort_order: 0, is_active: 1 }}
    fields={[
      { key: 'name', label: 'Product Name' },
      { key: 'category', label: 'Category' },
      { key: 'material', label: 'Material' },
      { key: 'dimensions', label: 'Dimensions (e.g. 72"L x 36"W x 30"H)' },
      { key: 'description', label: 'Description', type: 'textarea', full: true },
      { key: 'image_url', label: 'Main Image', type: 'image', full: true },
      { key: 'gallery_images', label: 'Gallery', type: 'gallery', full: true },
      { key: 'sort_order', label: 'Sort Order', type: 'number' },
    ]}
    renderThumb={item => item.image_url
      ? <img src={img(item.image_url)} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
      : <div className="w-16 h-16 rounded-lg bg-gray-100 flex-shrink-0" />}
  />
);

const ProcessTab = () => (
  <RepeaterTab
    resource="process"
    title="Step"
    emptyItem={{ step_no: 1, title: '', description: '', image_url: '', sort_order: 0, is_active: 1 }}
    fields={[
      { key: 'step_no', label: 'Step Number', type: 'number' },
      { key: 'title', label: 'Title' },
      { key: 'description', label: 'Description', type: 'textarea', full: true },
      { key: 'image_url', label: 'Image', type: 'image', full: true },
      { key: 'sort_order', label: 'Sort Order', type: 'number' },
    ]}
    renderThumb={item => item.image_url
      ? <img src={img(item.image_url)} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
      : <div className="w-16 h-16 rounded-lg bg-gray-100 flex-shrink-0" />}
  />
);

const TestimonialsTab = () => (
  <RepeaterTab
    resource="testimonials"
    title="Testimonial"
    emptyItem={{ client_name: '', client_role: '', quote: '', rating: 5, sort_order: 0, is_active: 1 }}
    fields={[
      { key: 'client_name', label: 'Client Name' },
      { key: 'client_role', label: 'Role / Company' },
      { key: 'rating', label: 'Rating (1-5)', type: 'number' },
      { key: 'quote', label: 'Quote', type: 'textarea', full: true },
    ]}
    renderThumb={item => (
      <div className="w-16 h-16 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 font-bold text-amber-700">
        {item.client_name?.charAt(0)}
      </div>
    )}
  />
);

const TeamTab = () => (
  <RepeaterTab
    resource="team"
    title="Team Member"
    emptyItem={{ name: '', designation: '', image_url: '', sort_order: 0, is_active: 1 }}
    fields={[
      { key: 'name', label: 'Full Name' },
      { key: 'designation', label: 'Role / Designation' },
      { key: 'image_url', label: 'Photo', type: 'image', full: true },
      { key: 'sort_order', label: 'Sort Order', type: 'number' },
    ]}
    renderThumb={item => item.image_url
      ? <img src={img(item.image_url)} alt="" className="w-16 h-16 rounded-full object-cover flex-shrink-0" />
      : <div className="w-16 h-16 rounded-full bg-gray-100 flex-shrink-0" />}
  />
);

// ═══════════════════════════════════════════════════════════════════════════
// PAGES TAB (dynamic pages)
// ═══════════════════════════════════════════════════════════════════════════
function PagesTab() {
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/website/admin/pages').then(res => setPages(res.data)).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const save = async () => {
    try {
      if (editing.id) await api.put(`/website/admin/pages/${editing.id}`, editing);
      else await api.post('/website/admin/pages', editing);
      toast.success('Page saved');
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    }
  };

  const remove = async (p) => {
    if (p.is_system) return toast.error('System pages cannot be deleted');
    if (!window.confirm('Delete this page?')) return;
    await api.delete(`/website/admin/pages/${p.id}`);
    toast.success('Deleted');
    load();
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <button onClick={() => setEditing({ slug: '', title: '', content: '', meta_title: '', meta_description: '', meta_keywords: '', is_published: 1, show_in_menu: 1, sort_order: 0 })}
          className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-5 py-2.5 rounded-xl font-bold text-sm">
          <Plus className="w-4 h-4" /> Add Page
        </button>
      </div>

      {editing && (
        <SectionCard title={editing.id ? 'Edit Page' : 'New Page'} action={<button onClick={() => setEditing(null)}><X className="w-4 h-4 text-gray-400" /></button>}>
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Page Title"><input className={inputCls} value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} /></Field>
            <Field label="URL Slug"><input className={inputCls} value={editing.slug} onChange={e => setEditing({ ...editing, slug: e.target.value })} placeholder="about-us" /></Field>
            <div className="md:col-span-2"><Field label="Page Content"><textarea rows={8} className={inputCls} value={editing.content || ''} onChange={e => setEditing({ ...editing, content: e.target.value })} /></Field></div>
            <Field label="Meta Title (SEO)"><input className={inputCls} value={editing.meta_title || ''} onChange={e => setEditing({ ...editing, meta_title: e.target.value })} /></Field>
            <Field label="Meta Keywords (SEO)"><input className={inputCls} value={editing.meta_keywords || ''} onChange={e => setEditing({ ...editing, meta_keywords: e.target.value })} /></Field>
            <div className="md:col-span-2"><Field label="Meta Description (SEO)"><textarea rows={2} className={inputCls} value={editing.meta_description || ''} onChange={e => setEditing({ ...editing, meta_description: e.target.value })} /></Field></div>
            <Field label="Published?">
              <select className={inputCls} value={editing.is_published ?? 1} onChange={e => setEditing({ ...editing, is_published: Number(e.target.value) })}>
                <option value={1}>Published</option>
                <option value={0}>Draft</option>
              </select>
            </Field>
            <Field label="Show in Menu?">
              <select className={inputCls} value={editing.show_in_menu ?? 1} onChange={e => setEditing({ ...editing, show_in_menu: Number(e.target.value) })}>
                <option value={1}>Yes</option>
                <option value={0}>No</option>
              </select>
            </Field>
          </div>
          <div className="flex justify-end mt-5">
            <button onClick={save} className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-6 py-2.5 rounded-xl font-bold text-sm">
              <Save className="w-4 h-4" /> Save Page
            </button>
          </div>
        </SectionCard>
      )}

      {loading ? <div className="text-gray-400 text-sm">Loading…</div> : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 divide-y divide-gray-100">
          {pages.map(p => (
            <div key={p.id} className="flex items-center justify-between px-5 py-4">
              <div>
                <div className="font-bold text-gray-900 text-sm flex items-center gap-2">
                  {p.title}
                  {p.is_system ? <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">system</span> : null}
                  {!p.is_published && <span className="text-[10px] bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">draft</span>}
                </div>
                <div className="text-xs text-gray-400">/{p.slug}</div>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setEditing(p)} className="text-xs font-semibold text-amber-700 flex items-center gap-1"><Pencil className="w-3 h-3" /> Edit</button>
                {!p.is_system && <button onClick={() => remove(p)} className="text-xs font-semibold text-red-600 flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MEDIA LIBRARY TAB
// ═══════════════════════════════════════════════════════════════════════════
function MediaTab() {
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/website/admin/media').then(res => setMedia(res.data)).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('image', file);
        await api.post('/website/admin/media/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success('Uploaded');
      load();
    } catch {
      toast.error('Upload failed');
    } finally { setUploading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this image?')) return;
    await api.delete(`/website/admin/media/${id}`);
    toast.success('Deleted');
    load();
  };

  return (
    <div className="space-y-5">
      <SectionCard title="Upload Images">
        <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-200 rounded-xl py-10 cursor-pointer hover:border-amber-400 transition-colors">
          {uploading ? <Loader2 className="w-6 h-6 text-amber-500 animate-spin" /> : <Upload className="w-6 h-6 text-gray-400" />}
          <span className="text-sm text-gray-500">{uploading ? 'Uploading…' : 'Click to upload one or more images'}</span>
          <input type="file" accept="image/*" multiple className="hidden" onChange={e => upload(e.target.files)} />
        </label>
      </SectionCard>

      {loading ? <div className="text-gray-400 text-sm">Loading…</div> : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {media.map(m => (
            <div key={m.id} className="relative group rounded-xl overflow-hidden border border-gray-100 aspect-square">
              <img src={img(m.url)} alt={m.alt_text} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <a href={img(m.url)} target="_blank" rel="noreferrer" className="bg-white p-2 rounded-full"><ExternalLink className="w-3.5 h-3.5" /></a>
                <button onClick={() => remove(m.id)} className="bg-white p-2 rounded-full"><Trash2 className="w-3.5 h-3.5 text-red-600" /></button>
              </div>
            </div>
          ))}
          {!media.length && <div className="col-span-4 text-gray-400 text-sm text-center py-8">No images uploaded yet.</div>}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ENQUIRIES TAB
// ═══════════════════════════════════════════════════════════════════════════
function EnquiriesTab() {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/website/admin/enquiries').then(res => setEnquiries(res.data)).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const setStatus = async (id, status) => {
    await api.patch(`/website/admin/enquiries/${id}`, { status });
    load();
  };

  const statusColor = { new: 'bg-blue-100 text-blue-700', contacted: 'bg-amber-100 text-amber-700', closed: 'bg-gray-100 text-gray-500' };

  if (loading) return <div className="text-gray-400 text-sm">Loading…</div>;
  if (!enquiries.length) return <div className="text-gray-400 text-sm bg-white rounded-2xl border border-gray-100 p-8 text-center">No enquiries yet — they'll appear here as visitors submit the contact form.</div>;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 divide-y divide-gray-100">
      {enquiries.map(e => (
        <div key={e.id} className="p-5 flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div>
            <div className="font-bold text-gray-900 text-sm">{e.name} <span className="text-gray-400 font-normal">— {e.enquiry_type}</span></div>
            <div className="text-xs text-gray-500 mt-0.5">{e.phone} {e.email ? `· ${e.email}` : ''}</div>
            {e.message && <div className="text-xs text-gray-600 mt-1.5 max-w-xl">{e.message}</div>}
            <div className="text-[11px] text-gray-400 mt-1.5">{new Date(e.created_at).toLocaleString()}</div>
          </div>
          <select value={e.status} onChange={ev => setStatus(e.id, ev.target.value)} className={`text-xs font-semibold rounded-full px-3 py-1.5 border-0 ${statusColor[e.status]}`}>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════
export default function WebsiteManagerPage() {
  const [tab, setTab] = useState('settings');

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Globe className="w-5 h-5 text-amber-500" /> Website</h1>
          <p className="text-sm text-gray-500 mt-0.5">Everything shown on moji.org.in is managed here — no code changes needed.</p>
        </div>
        <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs font-semibold text-amber-700 border border-amber-200 rounded-lg px-3 py-2 hover:bg-amber-50">
          <ExternalLink className="w-3.5 h-3.5" /> View Live Site
        </a>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1 mb-6">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-colors ${tab === t.key ? 'bg-amber-500 text-white' : 'bg-white text-gray-600 border border-gray-100 hover:bg-gray-50'}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'settings' && <SettingsTab />}
      {tab === 'homepage' && <HomepageTab />}
      {tab === 'team' && <TeamTab />}
      {tab === 'products' && <ProductsTab />}
      {tab === 'process' && <ProcessTab />}
      {tab === 'testimonials' && <TestimonialsTab />}
      {tab === 'pages' && <PagesTab />}
      {tab === 'media' && <MediaTab />}
      {tab === 'enquiries' && <EnquiriesTab />}
    </div>
  );
}
